import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  Geraeteauftrag,
  Kassenbuch,
  KassenApi,
  Tagesabschluss as Abschluss,
  Tagesbericht,
} from '../api';
import { aktiverBedienerName, aktuelleRechte } from '../bediener';
import {
  KASSENBUCH_SCHALTER,
  laufendeBuchung,
  neuerKassenbuchVorgang,
  sendeKassenbuchVorgang,
  ungeklaerteBuchung,
  verwirfKassenbuchVorgang,
  type KassenbuchVorgang,
} from '../kassenbuch-vorgang';
import {
  abschlussSchritte,
  abschlussVollstaendig,
  istMonatsletzter,
  NENNWERTE_CENT,
  parseAnzahl,
  TRINKGELD_GRUND,
  trinkgeldOffen,
  zaehlsumme,
  type Schritt,
  type Zaehlbrett,
} from '../tagesabschluss';
import type { GeraeteSpeicher } from '../geraetespeicher';
import { ladeTische } from '../tische';
import { pruefeAbschlussBereit, mitKassiersperre } from '../vorgang';
import { formatiereEuro } from '../warenkorb';

function heuteWien(): string {
  const teile = new Intl.DateTimeFormat('de-AT', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const t = (typ: string): string => teile.find((x) => x.type === typ)?.value ?? '';
  return `${t('year')}-${t('month')}-${t('day')}`;
}

function zeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' });
}

function vorzeichen(cent: number): string {
  return `${cent > 0 ? '+' : ''}${formatiereEuro(cent)}`;
}

const STATUS_ZEICHEN: Record<Schritt['status'], string> = {
  offen: '○',
  erledigt: '✓',
  uebersprungen: '–',
  warnung: '!',
};

/**
 * Geführter Tagesabschluss: offene Tische prüfen, Bargeld am Zählbrett zählen und
 * als Kassasturz buchen, Kassenschnitt am ZVT-Terminal, Tagesbericht ausgeben.
 * Erzeugt keine Belege; Monatsbeleg kommt vom Pflichtbeleg-Lauf des Servers.
 */
export function Tagesabschluss(props: {
  api: KassenApi;
  kassenId: string;
  zahlungsanbieter: string | null;
  bridgeZugelassen: boolean;
  monatsbelegFehltFuer: string | null;
  /**
   * Fehlerbericht 25.10.2026: Gerätespeicher mit den Vorgangsdatensätzen – ist ein Verkauf
   * dieser Kasse ungeklärt, wird der Tag nicht abgeschlossen (der Umsatz stünde noch nicht fest).
   */
  geraeteSpeicher?: GeraeteSpeicher;
}) {
  const { api, kassenId } = props;
  const datum = heuteWien();
  const [offeneTische, setOffeneTische] = useState<string[]>([]);
  const [kassenbuch, setKassenbuch] = useState<Kassenbuch | null>(null);
  const [brett, setBrett] = useState<Record<number, string>>({});
  const [zaehlMeldung, setZaehlMeldung] = useState<string | null>(null);
  const [terminal, setTerminal] = useState<Geraeteauftrag | null>(null);
  const [bericht, setBericht] = useState<Tagesbericht | null>(null);
  const [berichtAusgegeben, setBerichtAusgegeben] = useState(false);
  const [abschluesse, setAbschluesse] = useState<Abschluss[]>([]);
  /** Server-Sicht: seit dem letzten Abschluss kein Umsatzbeleg mehr? */
  const [abschlussAktuell, setAbschlussAktuell] = useState(true);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [druckMeldung, setDruckMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /**
   * A5-M7 (Welle 2): Kassenbuch-Buchung (Kassasturz, Trinkgeld-Entnahme) läuft – synchron
   * gesperrt, damit ein Doppeltipp vor dem nächsten Rendern nicht zweimal bucht; beide Tasten
   * sind bis nach dem Neuladen des Kassenbuchs deaktiviert (sonst stünde der alte Rest noch da).
   */
  // Codex W2-A2 R2/R3: Eine beim Reiterwechsel noch laufende Buchung (Sitzungsregister) wird
  // übernommen – gesperrt, bis ihr Ausgang feststeht und das Kassenbuch neu geladen ist, nie als
  // „ungeklärt“ angeboten. Der beim ersten Rendern gesehene Lauf wird festgehalten (endet er vor
  // dem Effekt, wird trotzdem abgeglichen).
  const uebernommenerLauf = useRef(laufendeBuchung(kassenId));
  const buchungSperre = useRef(uebernommenerLauf.current !== null);
  const [buchungLaeuft, setBuchungLaeuft] = useState(() => uebernommenerLauf.current !== null);
  /**
   * A5-M7: Kassenbuch-Buchung ohne Antwort (Ausgang unklar) – bis zur Klärung („erneut senden“
   * mit derselben Kennung bzw. ausdrücklich verwerfen) keine neue Kassenbuch-Buchung.
   */
  const [ungeklaert, setUngeklaert] = useState<KassenbuchVorgang | null>(() =>
    uebernommenerLauf.current === null ? ungeklaerteBuchung(localStorage, kassenId) : null,
  );
  /**
   * Codex W2-A2 R3: Ladevorgänge können sich überholen (Neuladen nach einer Buchung, während ein
   * älterer noch läuft). Je Bereich gilt nur ein Ergebnis, das jünger ist als das zuletzt
   * übernommene – ein älterer Stand (z. B. ohne die eben gebuchte Entnahme) überschreibt nie
   * einen neueren.
   */
  const ladeNr = useRef(0);
  const uebernommenNr = useRef<Record<string, number>>({});

  const terminalNoetig = props.zahlungsanbieter === 'zvt' && props.bridgeZugelassen;

  const laden = useCallback(async () => {
    const nr = ++ladeNr.current;
    /** Ergebnis für `bereich` nur übernehmen, wenn kein jüngerer Ladevorgang schon übernommen ist. */
    const uebernimm = (bereich: string, setzen: () => void): void => {
      if (nr > (uebernommenNr.current[bereich] ?? 0)) {
        uebernommenNr.current[bereich] = nr;
        setzen();
      }
    };
    setFehler(null);
    const lokal = ladeTische(localStorage, kassenId).map((t) => `${t.name} (lokal)`);
    try {
      const server = await api.tische(kassenId);
      uebernimm('tische', () => {
        setOffeneTische([...server.map((t) => t.name), ...lokal]);
      });
    } catch {
      uebernimm('tische', () => {
        setOffeneTische(lokal);
      });
    }
    try {
      const kb = await api.kassenbuch(kassenId, datum);
      uebernimm('kassenbuch', () => {
        setKassenbuch(kb);
      });
    } catch {
      uebernimm('kassenbuch', () => {
        setKassenbuch(null);
      });
    }
    try {
      const b = await api.tagesbericht(kassenId, datum);
      uebernimm('bericht', () => {
        setBericht(b);
      });
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
    try {
      const a = (await api.tagesabschluesse(kassenId, datum, datum)).abschluesse;
      const aktuell = (await api.status(kassenId)).tagesabschluss?.aktuell ?? true;
      uebernimm('abschluesse', () => {
        setAbschluesse(a);
        setAbschlussAktuell(aktuell);
      });
    } catch {
      uebernimm('abschluesse', () => {
        setAbschluesse([]); // ohne Protokoll-Modul (501)
      });
    }
  }, [api, kassenId, datum]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Übernommene laufende Buchung: nach ihrem Ausgang Stand übernehmen und neu laden; die Sperre
  // erst danach lösen (Codex R3: sonst wäre der alte Trinkgeldrest kurz wieder auszahlbar)
  useEffect(() => {
    const lauf = uebernommenerLauf.current;
    if (lauf === null) {
      return;
    }
    /** Ansicht noch eingehängt (Objekt: der Wert ändert sich in der Bereinigung). */
    const ansicht = { aktiv: true };
    const eingehaengt = (): boolean => ansicht.aktiv;
    void lauf.then(async () => {
      if (!eingehaengt()) {
        return;
      }
      setUngeklaert(ungeklaerteBuchung(localStorage, kassenId));
      try {
        await laden();
      } finally {
        if (eingehaengt()) {
          uebernommenerLauf.current = null;
          buchungSperre.current = false;
          setBuchungLaeuft(false);
        }
      }
    });
    return () => {
      ansicht.aktiv = false;
    };
  }, [kassenId, laden]);

  const zaehlbrett = useMemo<Zaehlbrett>(
    () =>
      Object.fromEntries(
        Object.entries(brett).map(([wert, text]) => [Number(wert), parseAnzahl(text)]),
      ),
    [brett],
  );
  const gezaehltCent = zaehlsumme(zaehlbrett);
  const sollCent = kassenbuch?.bestand.sollCent ?? null;
  const letzteZaehlungHeute = kassenbuch?.eintraege.filter((e) => e.art === 'zaehlung').at(-1);
  /**
   * Noch nicht ausgezahltes Kartentrinkgeld (bereits gebuchte Entnahmen abgezogen); `null`, solange
   * das Kassenbuch nicht geladen ist (Codex W2-A2 R1: ohne die gebuchten Entnahmen erschiene das
   * schon ausgezahlte Trinkgeld wieder als offen).
   */
  const trinkgeldRest =
    bericht === null
      ? 0
      : kassenbuch === null
        ? null
        : trinkgeldOffen(bericht.trinkgeldCent, kassenbuch.eintraege);

  const letzterAbschluss = abschluesse[0] ?? null;
  const protokollVeraltet = letzterAbschluss !== null && !abschlussAktuell;

  const schritte = abschlussSchritte({
    offeneTische: offeneTische.length,
    zaehlungHeute: letzteZaehlungHeute === undefined ? null : zeit(letzteZaehlungHeute.erstelltAm),
    terminalNoetig,
    terminalStatus: terminal?.status ?? null,
    berichtAusgegeben,
    protokolliert: letzterAbschluss === null ? null : zeit(letzterAbschluss.erstelltAm),
    protokollVeraltet,
  });
  const vollstaendig = abschlussVollstaendig(schritte);

  /**
   * A5-M7: Kassenbuch-Buchung mit ihrer Vorgangskennung senden – vorher gemerkt (sonst nicht
   * gesendet). Eindeutige Antwort (Erfolg, Ablehnung) löscht den Merker; bei unklarem Ausgang
   * bleibt die Buchung ungeklärt (nur „erneut senden“ verwendet Kennung und Inhalt wieder).
   */
  async function kassenbuchSenden(v: KassenbuchVorgang): ReturnType<KassenApi['kassenbuchBuchen']> {
    try {
      return await sendeKassenbuchVorgang(
        localStorage,
        kassenId,
        v,
        (x, wiederholung) => {
          const eintrag = {
            art: x.art,
            betragCent: x.betragCent,
            grund: x.grund,
            vorgangId: x.vorgangId,
          };
          return wiederholung
            ? api.kassenbuchWiederholen(kassenId, eintrag)
            : api.kassenbuchBuchen(kassenId, eintrag);
        },
        () => api.kassenbuchErkenntWiederholung(kassenId, datum),
      );
    } finally {
      setUngeklaert(ungeklaerteBuchung(localStorage, kassenId));
    }
  }

  /** Rückmeldung nach einer gelungenen Buchung (auch nach „erneut senden“). */
  function gebucht(r: Awaited<ReturnType<KassenApi['kassenbuchBuchen']>>): void {
    const e = r.eintrag;
    if (e.art === 'zaehlung') {
      setZaehlMeldung(
        e.sollCent !== null && e.differenzCent !== null
          ? `Gezählt ${formatiereEuro(e.betragCent)} €, Soll ${formatiereEuro(e.sollCent)} €, Differenz ${vorzeichen(e.differenzCent)} €`
          : `Gezählt ${formatiereEuro(e.betragCent)} € gebucht`,
      );
      setBrett({});
    } else {
      setZaehlMeldung(`Trinkgeld ${formatiereEuro(e.betragCent)} € als Entnahme gebucht`);
    }
  }

  /** A5-M7: eine Kassenbuch-Buchung zugleich; Sperre bis nach dem Neuladen. */
  async function mitBuchungssperre(aufgabe: () => Promise<void>): Promise<void> {
    if (buchungSperre.current || laufendeBuchung(kassenId) !== null) {
      return;
    }
    buchungSperre.current = true;
    setBuchungLaeuft(true);
    try {
      await aufgabe();
    } finally {
      buchungSperre.current = false;
      setBuchungLaeuft(false);
    }
  }

  function kassasturzBuchen(): Promise<void> {
    return mitBuchungssperre(kassasturzBuchenEinmal);
  }

  async function kassasturzBuchenEinmal(): Promise<void> {
    if (ungeklaert !== null) {
      return;
    }
    setZaehlMeldung(null);
    setFehler(null);
    if (gezaehltCent <= 0 && !window.confirm('Gezählter Bestand ist 0,00 € – trotzdem buchen?')) {
      return;
    }
    try {
      gebucht(
        await kassenbuchSenden(
          neuerKassenbuchVorgang({
            art: 'zaehlung',
            betragCent: gezaehltCent,
            grund: 'Tagesabschluss',
          }),
        ),
      );
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  /** Trinkgeld der Kartenzahlungen des Tages als Entnahme buchen (Auszahlung an die Bediener). */
  function trinkgeldAuszahlen(): Promise<void> {
    return mitBuchungssperre(trinkgeldAuszahlenEinmal);
  }

  async function trinkgeldAuszahlenEinmal(): Promise<void> {
    if (bericht === null || trinkgeldRest === null || trinkgeldRest <= 0 || ungeklaert !== null) {
      return;
    }
    const wer = bericht.nachBediener
      .filter((k) => k.trinkgeldCent > 0)
      .map(
        (k) =>
          `${k.bediener === '' ? 'ohne Bediener' : k.bediener} ${formatiereEuro(k.trinkgeldCent)} €`,
      )
      .join(', ');
    if (
      !window.confirm(`Trinkgeld ${formatiereEuro(trinkgeldRest)} € als Entnahme buchen?\n${wer}`)
    ) {
      return;
    }
    setFehler(null);
    try {
      gebucht(
        await kassenbuchSenden(
          neuerKassenbuchVorgang({
            art: 'entnahme',
            betragCent: trinkgeldRest,
            grund: `${TRINKGELD_GRUND} (${wer})`.slice(0, 200),
          }),
        ),
      );
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * A5-M7: ungeklärte Buchung unverändert erneut senden (dieselbe Kennung, derselbe Inhalt). Ein
   * Server mit Vorgangsprüfung (17f2d82) bucht sie höchstens einmal. Gesendet wird nur, wenn der
   * Server unmittelbar davor seine Vorgangsprüfung meldet (E-W1-RKSV F2, Rollback-Untergrenze);
   * sonst bleibt die Buchung ungeklärt (`KASSENBUCH_SCHALTER`, `sendeKassenbuchVorgang`).
   */
  function ungeklaerteErneutSenden(): Promise<void> {
    return mitBuchungssperre(async () => {
      const v = ungeklaert;
      if (v === null || !KASSENBUCH_SCHALTER.wiederholungAktiv) {
        return;
      }
      if (
        !window.confirm(
          'Buchung erneut senden? Vorher „Aktualisieren“: Steht sie schon im Kassenbuch, nicht erneut senden, sondern verwerfen.',
        )
      ) {
        return;
      }
      setFehler(null);
      setZaehlMeldung(null);
      try {
        gebucht(await kassenbuchSenden(v));
        await laden();
      } catch (e: unknown) {
        setFehler(e instanceof Error ? e.message : String(e));
      }
    });
  }

  /** A5-M7: ungeklärte Buchung ausdrücklich verwerfen (gebucht bzw. sicher nicht angekommen). */
  function ungeklaerteVerwerfen(): void {
    const v = ungeklaert;
    if (v === null || buchungSperre.current || laufendeBuchung(kassenId) !== null) {
      return;
    }
    if (
      !window.confirm(
        'Ungeklärte Buchung verwerfen? Nur, wenn das Kassenbuch nach „Aktualisieren“ zeigt, dass sie gebucht ist – oder sicher ist, dass sie nicht angekommen ist.',
      )
    ) {
      return;
    }
    verwirfKassenbuchVorgang(localStorage, kassenId, v.vorgangId);
    setUngeklaert(ungeklaerteBuchung(localStorage, kassenId));
  }

  async function kassenschnitt(): Promise<void> {
    setFehler(null);
    setLaeuft(true);
    try {
      let a = await api.terminalTagesabschluss(kassenId);
      setTerminal(a);
      const frist = Date.now() + 120_000;
      while ((a.status === 'offen' || a.status === 'uebernommen') && Date.now() < frist) {
        await new Promise((r) => setTimeout(r, 1_000));
        a = await api.terminalAuftrag(kassenId, a.auftragId);
        setTerminal(a);
      }
      if (a.status === 'offen' || a.status === 'uebernommen') {
        setFehler('Kassenschnitt noch nicht abgeschlossen – läuft die Bridge im Laden?');
      }
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  /** Recht „Tagesabschluss“ des angemeldeten Bedieners (ohne Verwaltung: alle). */
  const darfAbschliessen = aktuelleRechte(sessionStorage, kassenId).tagesabschluss;

  /** Abschluss am Server festhalten: Bediener, Kassasturz und Kassenschnitt dieser Sitzung mitgeben. */
  async function protokollieren(): Promise<void> {
    setFehler(null);
    setMeldung(null);
    const bediener = aktiverBedienerName(sessionStorage, localStorage, kassenId);
    try {
      const senden = () =>
        api.tagesabschlussProtokollieren(kassenId, {
          datum,
          ...(bediener === null ? {} : { bediener }),
          ...(letzteZaehlungHeute === undefined ? {} : { zaehlungId: letzteZaehlungHeute.id }),
          ...(terminal?.status === 'erledigt' ? { terminalAuftragId: terminal.auftragId } : {}),
        });
      const speicher = props.geraeteSpeicher;
      // Codex-Bestätigungsprüfung F5: Prüfung und Protokoll unter der Kassiersperre (kein
      // anderes Fenster legt dazwischen einen Vorgang an); auch noch nicht nachsignierte
      // Offline-Verkäufe sperren den Abschluss
      const r =
        speicher === undefined
          ? await senden()
          : await mitKassiersperre(kassenId, async () => {
              await pruefeAbschlussBereit(speicher, kassenId);
              return senden();
            });
      const mail =
        r.email === null
          ? ''
          : r.email.fehler === undefined
            ? ` · Tagesbericht per E-Mail an ${r.email.an.join(', ')}`
            : ` · E-Mail-Versand fehlgeschlagen: ${r.email.fehler}`;
      setMeldung(
        `Tagesabschluss ${r.abschluss.datum} um ${zeit(r.abschluss.erstelltAm)} protokolliert${mail}`,
      );
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function bonDrucken(): Promise<void> {
    setDruckMeldung(null);
    try {
      const a = await api.tagesberichtDruckauftrag(kassenId, datum);
      setDruckMeldung(`Druckauftrag ${a.auftragId} an die Bridge übergeben`);
      setBerichtAusgegeben(true);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const terminalZeilen = Array.isArray(terminal?.details.belegzeilen)
    ? (terminal.details.belegzeilen as unknown[]).filter((z): z is string => typeof z === 'string')
    : [];

  return (
    <>
      <div className="karte kein-druck">
        <h2>Tagesabschluss {datum}</h2>
        <ol className="abschluss-schritte">
          {schritte.map((s) => (
            <li key={s.id} className={`schritt ${s.status}`}>
              <span className="schritt-zeichen">{STATUS_ZEICHEN[s.status]}</span>
              <span>
                <strong>{s.titel}</strong> – {s.text}
              </span>
            </li>
          ))}
        </ol>
        {vollstaendig && <p className="ok">Tagesabschluss vollständig.</p>}
        {props.monatsbelegFehltFuer !== null && (
          <p className="warnung">
            Monatsbeleg für {props.monatsbelegFehltFuer} fehlt – der Pflichtbeleg-Lauf des Servers
            erstellt ihn; Stand im Reiter „Status" prüfen.
          </p>
        )}
        {istMonatsletzter(datum) && props.monatsbelegFehltFuer === null && (
          <p className="hinweis">
            Monatsende: Der Monatsbeleg (Nullbeleg) wird nach dem letzten Umsatz des Monats vom
            Server erstellt
            {datum.endsWith('-12-31')
              ? ' – im Dezember als Jahresbeleg: ausdrucken, prüfen (Belegcheck-App) und aufbewahren.'
              : '.'}
          </p>
        )}
        {fehler !== null && <p className="fehler">{fehler}</p>}
        <button
          className="taste zweit"
          onClick={() => {
            void laden();
          }}
        >
          Aktualisieren
        </button>
      </div>

      {offeneTische.length > 0 && (
        <div className="karte kein-druck">
          <h3>1 · Offene Tische</h3>
          <p className="warnung">{offeneTische.join(', ')}</p>
          <p className="hinweis">
            Im Reiter „Verkauf" aufrufen und kassieren oder verwerfen; geparkte Bons sind keine
            Belege.
          </p>
        </div>
      )}

      <div className="karte kein-druck">
        <h3>2 · Kassasturz (Zählbrett)</h3>
        {sollCent !== null ? (
          <p>
            Rechnerischer Bargeldbestand: <strong>{formatiereEuro(sollCent)} €</strong>
            {letzteZaehlungHeute !== undefined && (
              <>
                {' '}
                · heute bereits gezählt {formatiereEuro(letzteZaehlungHeute.betragCent)} € (
                {zeit(letzteZaehlungHeute.erstelltAm)})
              </>
            )}
          </p>
        ) : (
          <p className="hinweis">Kassenbuch nicht verfügbar – Zählung wird ohne Soll gebucht.</p>
        )}
        <div className="zaehlbrett">
          {NENNWERTE_CENT.map((wert) => (
            <label key={wert}>
              {formatiereEuro(wert)} €
              <input
                inputMode="numeric"
                placeholder="0"
                value={brett[wert] ?? ''}
                onChange={(e) => {
                  const text = e.target.value;
                  setBrett((b) => ({ ...b, [wert]: text }));
                }}
              />
            </label>
          ))}
        </div>
        <div className="zeile">
          <span className="summe">Gezählt {formatiereEuro(gezaehltCent)} €</span>
          {sollCent !== null && gezaehltCent > 0 && (
            <span className={gezaehltCent - sollCent === 0 ? 'ok' : 'warnung'}>
              Differenz {vorzeichen(gezaehltCent - sollCent)} €
            </span>
          )}
          {bericht !== null && bericht.trinkgeldCent > 0 && (
            <button
              className="taste zweit"
              disabled={
                trinkgeldRest === null || trinkgeldRest <= 0 || buchungLaeuft || ungeklaert !== null
              }
              aria-busy={buchungLaeuft}
              title={
                trinkgeldRest === null
                  ? 'Kassenbuch nicht geladen – erst „Aktualisieren“'
                  : 'Trinkgeld der Kartenzahlungen aus der Kassa an die Bediener auszahlen (Kassenbuch-Entnahme)'
              }
              onClick={() => void trinkgeldAuszahlen()}
            >
              {trinkgeldRest === null
                ? 'Trinkgeld auszahlen'
                : trinkgeldRest > 0
                  ? `Trinkgeld auszahlen (${formatiereEuro(trinkgeldRest)} €)`
                  : 'Trinkgeld ausgezahlt'}
            </button>
          )}
          <button
            className="taste"
            disabled={!darfAbschliessen || buchungLaeuft || ungeklaert !== null}
            aria-busy={buchungLaeuft}
            title={
              darfAbschliessen ? undefined : 'Kein Abschlussrecht für den angemeldeten Bediener'
            }
            onClick={() => void kassasturzBuchen()}
          >
            Kassasturz buchen
          </button>
          <button
            className="taste zweit"
            onClick={() => {
              setBrett({});
            }}
          >
            Leeren
          </button>
        </div>
        {zaehlMeldung !== null && <p className="ok">{zaehlMeldung}</p>}
        {ungeklaert !== null && (
          <>
            <p className="warnung">
              {ungeklaert.art === 'zaehlung' ? 'Kassasturz' : 'Entnahme'}{' '}
              {formatiereEuro(ungeklaert.betragCent)} € ({zeit(ungeklaert.seit)}) ungeklärt – die
              Antwort der Kasse fehlt.{' '}
              {KASSENBUCH_SCHALTER.wiederholungAktiv
                ? 'Erst „Aktualisieren“ und im Kassenbuch prüfen, dann erneut senden oder verwerfen; bis dahin keine neue Kassenbuch-Buchung.'
                : 'Bitte „Aktualisieren“ und prüfen, ob die Buchung im Kassenbuch steht. Steht sie dort, den Merker verwerfen. Fehlt sie, ist das noch kein Beweis – die Anfrage kann noch unterwegs sein: später erneut prüfen; erst wenn sicher ist, dass sie nicht gebucht wurde, Merker verwerfen und neu buchen. Bis dahin keine neue Kassenbuch-Buchung.'}
            </p>
            <div className="zeile">
              {KASSENBUCH_SCHALTER.wiederholungAktiv && (
                <button
                  className="taste zweit aktiv"
                  disabled={buchungLaeuft}
                  aria-busy={buchungLaeuft}
                  onClick={() => void ungeklaerteErneutSenden()}
                >
                  Buchung erneut senden
                </button>
              )}
              <button
                className="taste zweit"
                disabled={buchungLaeuft}
                onClick={() => {
                  ungeklaerteVerwerfen();
                }}
              >
                Ungeklärte Buchung verwerfen
              </button>
            </div>
          </>
        )}
      </div>

      {terminalNoetig && (
        <div className="karte kein-druck">
          <h3>3 · Kassenschnitt am Kartenterminal</h3>
          <p className="hinweis">
            Löst den Tagesabschluss (ZVT 06 50) am Terminal dieser Kasse über die Bridge aus; das
            Terminal druckt die Tagessummen.
          </p>
          <button
            className="taste"
            disabled={laeuft || !darfAbschliessen}
            title={
              darfAbschliessen ? undefined : 'Kein Abschlussrecht für den angemeldeten Bediener'
            }
            onClick={() => {
              void kassenschnitt();
            }}
          >
            {laeuft ? 'Läuft …' : 'Kassenschnitt auslösen'}
          </button>
          {terminal !== null && (
            <p className={terminal.status === 'fehlgeschlagen' ? 'fehler' : 'hinweis'}>
              Auftrag {terminal.auftragId}: {terminal.status}
              {typeof terminal.details.fehler === 'string' && ` – ${terminal.details.fehler}`}
            </p>
          )}
          {terminalZeilen.length > 0 && <pre className="bon">{terminalZeilen.join('\n')}</pre>}
        </div>
      )}

      <div className="karte">
        <h3>4 · Tagesbericht</h3>
        {bericht === null ? (
          <p className="hinweis">Tagesbericht noch nicht geladen.</p>
        ) : (
          <table className="positionen">
            <tbody>
              <tr>
                <th>Umsatz brutto (Standard + Storno)</th>
                <td className="betrag">{formatiereEuro(bericht.umsatz.bruttoCent)} €</td>
              </tr>
              <tr>
                <th>Bar ({bericht.umsatz.nachZahlungsart.bar.anzahl} Belege)</th>
                <td className="betrag">
                  {formatiereEuro(bericht.umsatz.nachZahlungsart.bar.bruttoCent)} €
                </td>
              </tr>
              <tr>
                <th>Karte ({bericht.umsatz.nachZahlungsart.karte.anzahl} Belege)</th>
                <td className="betrag">
                  {formatiereEuro(bericht.umsatz.nachZahlungsart.karte.bruttoCent)} €
                </td>
              </tr>
              {bericht.umsatz.nachZahlungsart.gutschein.anzahl > 0 && (
                <tr>
                  <th>Gutschein ({bericht.umsatz.nachZahlungsart.gutschein.anzahl} Belege)</th>
                  <td className="betrag">
                    {formatiereEuro(bericht.umsatz.nachZahlungsart.gutschein.bruttoCent)} €
                  </td>
                </tr>
              )}
              {bericht.umsatz.nachSteuersatz.map((z) => (
                <tr key={z.steuersatz}>
                  <th>
                    {z.steuersatz} % (netto {formatiereEuro(z.nettoCent)} €, USt{' '}
                    {formatiereEuro(z.ustCent)} €)
                  </th>
                  <td className="betrag">{formatiereEuro(z.bruttoCent)} €</td>
                </tr>
              ))}
              <tr>
                <th>Belege gesamt / Storno / Training</th>
                <td className="betrag">
                  {bericht.anzahl.gesamt} / {bericht.anzahl.storno} / {bericht.anzahl.training}
                </td>
              </tr>
              {bericht.anzahl.ausfallbelege > 0 && (
                <tr>
                  <th>Ausfallbelege (Sicherheitseinrichtung ausgefallen)</th>
                  <td className="betrag">{bericht.anzahl.ausfallbelege}</td>
                </tr>
              )}
              <tr>
                <th>Einlagen / Entnahmen</th>
                <td className="betrag">
                  {formatiereEuro(bericht.bargeld.einlagenCent)} € /{' '}
                  {formatiereEuro(bericht.bargeld.entnahmenCent)} €
                </td>
              </tr>
              {bericht.bargeld.zaehlungen.map((z) => (
                <tr key={z.zeitpunkt}>
                  <th>Kassasturz {zeit(z.zeitpunkt)} (Ist / Soll / Differenz)</th>
                  <td className="betrag">
                    {formatiereEuro(z.istCent)} € /{' '}
                    {z.sollCent === null ? '–' : `${formatiereEuro(z.sollCent)} €`} /{' '}
                    {z.differenzCent === null ? '–' : `${vorzeichen(z.differenzCent)} €`}
                  </td>
                </tr>
              ))}
              {bericht.umsatzzaehler !== null && (
                <tr>
                  <th>Umsatzzähler Anfang → Ende</th>
                  <td className="betrag">
                    {formatiereEuro(bericht.umsatzzaehler.anfangCent)} €{' → '}
                    {formatiereEuro(bericht.umsatzzaehler.endeCent)} €
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        <div className="zeile kein-druck">
          <button
            className="taste"
            disabled={bericht === null}
            onClick={() => {
              window.print();
              setBerichtAusgegeben(true);
            }}
          >
            Drucken
          </button>
          {props.bridgeZugelassen && (
            <button
              className="taste zweit"
              disabled={bericht === null}
              onClick={() => void bonDrucken()}
            >
              Bon drucken (Bridge)
            </button>
          )}
        </div>
        {druckMeldung !== null && <p className="hinweis">{druckMeldung}</p>}
      </div>

      <div className="karte kein-druck">
        <h3>5 · Abschluss protokollieren</h3>
        <p className="hinweis">
          Hält den Abschluss mit dem Tagesbericht als Momentaufnahme am Server fest (append-only);
          Kassasturz und Kassenschnitt dieser Sitzung werden verknüpft. Wird danach noch kassiert,
          einfach einen Nachtrag protokollieren.
        </p>
        {letzterAbschluss !== null && (
          <p className={protokollVeraltet ? 'warnung' : 'ok'}>
            Zuletzt protokolliert um {zeit(letzterAbschluss.erstelltAm)}
            {letzterAbschluss.bediener !== null && ` von ${letzterAbschluss.bediener}`} · Umsatz{' '}
            {formatiereEuro(letzterAbschluss.bericht.umsatz.bruttoCent)} €
            {protokollVeraltet && ' – seither wurde kassiert'}
          </p>
        )}
        <button
          className="taste"
          disabled={bericht === null || !darfAbschliessen}
          title={darfAbschliessen ? undefined : 'Kein Abschlussrecht für den angemeldeten Bediener'}
          onClick={() => void protokollieren()}
        >
          {letzterAbschluss === null ? 'Abschluss protokollieren' : 'Nachtrag protokollieren'}
        </button>
        {meldung !== null && <p className="hinweis">{meldung}</p>}
      </div>
    </>
  );
}
